import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { signInWithPassword, signInWithOtp } from '@/lib/api/auth';
import { isSupabaseConfigured } from '@/lib/supabase/client';
import { cn } from '@/lib/utils/cn';
import { Capacitor } from '@capacitor/core';

// Network errors ("Load failed", "Failed to fetch") mean the Supabase URL is
// unreachable — translate them into a human-readable message.
function toAuthErrorMessage(err: unknown, fallback: string): string {
  const msg = err instanceof Error ? err.message : typeof err === 'object' && err !== null && 'message' in err ? String((err as { message: unknown }).message) : '';
  if (msg === 'Load failed' || msg === 'Failed to fetch' || msg.toLowerCase().includes('networkerror')) {
    return 'Unable to reach the server. Check your connection and try again.';
  }
  return msg || fallback;
}

type Mode = 'password' | 'magic';
type LocationState = { from?: { pathname: string } } | null;

const passwordSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'At least 8 characters'),
});
const magicSchema = z.object({
  email: z.string().email('Enter a valid email'),
});
type PasswordValues = z.infer<typeof passwordSchema>;
type MagicValues = z.infer<typeof magicSchema>;

export function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('password');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);
  const isNative = Capacitor.isNativePlatform();

  const redirectTo =
    (location.state as LocationState)?.from?.pathname ?? '/feed';

  const passwordForm = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
  });
  const magicForm = useForm<MagicValues>({
    resolver: zodResolver(magicSchema),
  });

  const onPassword = async (values: PasswordValues) => {
    setSubmitError(null);
    try {
      await signInWithPassword(values.email, values.password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setSubmitError(toAuthErrorMessage(err, 'Sign in failed.'));
    }
  };

  const onMagic = async (values: MagicValues) => {
    setSubmitError(null);
    try {
      await signInWithOtp(values.email);
      setMagicSent(true);
    } catch (err) {
      setSubmitError(toAuthErrorMessage(err, 'Could not send link.'));
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-cream px-4"
      style={{
        paddingTop: 'max(env(safe-area-inset-top), 3rem)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 3rem)',
      }}
    >
      <div className="w-full max-w-md">
        {isNative ? (
          <div className="mb-8 flex items-center justify-center gap-3 text-navy">
            <img src="/brand/cpn_mark.svg" alt="" aria-hidden="true" className="h-10 w-10" />
            <span className="font-display text-base font-semibold">
              The Cambodian Professionals Network
            </span>
          </div>
        ) : (
          <Link
            to="/"
            className="mb-8 flex items-center justify-center gap-3 text-navy"
          >
            <img src="/brand/cpn_mark.svg" alt="" aria-hidden="true" className="h-10 w-10" />
            <span className="font-display text-base font-semibold">
              The Cambodian Professionals Network
            </span>
          </Link>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>
              Welcome back. Sign in to see the member map and directory.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!isSupabaseConfigured && (
              <p className="mb-4 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
                Backend not configured — set <code>VITE_SUPABASE_URL</code> and{' '}
                <code>VITE_SUPABASE_ANON_KEY</code> in your environment.
              </p>
            )}
            {/* Mode toggle */}
            <div
              role="tablist"
              aria-label="Sign-in method"
              className="mb-6 flex rounded-lg border border-border bg-cream-warm/40 p-1"
            >
              {(
                [
                  { value: 'password', label: 'Password' },
                  { value: 'magic', label: 'Magic link' },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.value}
                  role="tab"
                  type="button"
                  aria-selected={mode === tab.value}
                  onClick={() => {
                    setMode(tab.value);
                    setSubmitError(null);
                    setMagicSent(false);
                  }}
                  className={cn(
                    'flex-1 rounded-md py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40',
                    mode === tab.value
                      ? 'bg-white text-navy shadow-sm'
                      : 'text-muted hover:text-navy',
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Password form */}
            {mode === 'password' && (
              <form
                onSubmit={passwordForm.handleSubmit(onPassword)}
                noValidate
                className="flex flex-col gap-4"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={!!passwordForm.formState.errors.email}
                    {...passwordForm.register('email')}
                  />
                  {passwordForm.formState.errors.email && (
                    <p role="alert" aria-live="polite" className="text-xs text-destructive">
                      {passwordForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-gold-dark hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    aria-invalid={!!passwordForm.formState.errors.password}
                    {...passwordForm.register('password')}
                  />
                  {passwordForm.formState.errors.password && (
                    <p role="alert" aria-live="polite" className="text-xs text-destructive">
                      {passwordForm.formState.errors.password.message}
                    </p>
                  )}
                </div>
                {submitError && (
                  <p role="alert" aria-live="polite"
                    className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    {submitError}
                  </p>
                )}
                <Button type="submit" disabled={passwordForm.formState.isSubmitting}>
                  {passwordForm.formState.isSubmitting ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>
            )}

            {/* Magic link form */}
            {mode === 'magic' && !magicSent && (
              <form
                onSubmit={magicForm.handleSubmit(onMagic)}
                noValidate
                className="flex flex-col gap-4"
              >
                <p className="text-sm text-muted">
                  We'll email you a one-time sign-in link. No password needed.
                </p>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="magic-email">Email</Label>
                  <Input
                    id="magic-email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={!!magicForm.formState.errors.email}
                    {...magicForm.register('email')}
                  />
                  {magicForm.formState.errors.email && (
                    <p role="alert" aria-live="polite" className="text-xs text-destructive">
                      {magicForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                {submitError && (
                  <p role="alert" aria-live="polite"
                    className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    {submitError}
                  </p>
                )}
                <Button type="submit" variant="gold" disabled={magicForm.formState.isSubmitting}>
                  {magicForm.formState.isSubmitting ? 'Sending…' : 'Send magic link'}
                </Button>
              </form>
            )}

            {/* Magic link sent */}
            {mode === 'magic' && magicSent && (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <span className="text-2xl" aria-hidden="true">✉️</span>
                <p className="font-display text-base text-navy">Check your email</p>
                <p className="text-sm text-muted">
                  We sent a sign-in link to{' '}
                  <strong className="text-navy">
                    {magicForm.getValues('email')}
                  </strong>
                  . Click it to sign in.
                </p>
                <button
                  type="button"
                  onClick={() => setMagicSent(false)}
                  className="text-xs text-gold-dark hover:underline"
                >
                  Use a different email
                </button>
              </div>
            )}

            <p className="mt-6 text-center text-sm text-muted">
              New here?{' '}
              <Link to="/signup" className="font-medium text-gold-dark hover:underline">
                Create an account
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
